import { T, Var } from 'gt-react';
import type { LegPriceBreak } from '@erp/shared-types';
import { formatCurrency } from '@erp/shared-utils';
import { agreementSavingOf } from './agreementSaving';

/**
 * One line under a board row's contract badge: what the agreement saves on
 * the line against the supplier product row's own price for the same
 * quantity, or, in amber, what it costs over it. Nothing when no agreement
 * priced the line, when it saves exactly nothing, or when the run did not
 * record the row's price. The detail modal's contract block has the rest.
 */

const NOTE_CLASS = 'whitespace-nowrap text-[11px] font-medium tabular-nums';

export function AgreementSavingNote({
  priceBreak,
  quantity,
}: {
  priceBreak: LegPriceBreak | null | undefined;
  quantity: number | null | undefined;
}) {
  const saving = agreementSavingOf({ priceBreak, quantity });
  if (saving === null || saving.totalSaving === 0) return null;
  const onList = saving.withoutAgreementCostSource === 'list';

  if (saving.totalSaving > 0) {
    const total = formatCurrency(saving.totalSaving);
    return (
      <span className={`${NOTE_CLASS} text-green-700`}>
        {onList ? (
          <T context="replenishment board note under a contract badge: what the contract saves on this line against the supplier's list cost">
            saves <Var>{total}</Var> vs list
          </T>
        ) : (
          <T context="replenishment board note under a contract badge: what the contract saves on this line against the supplier's price-break cost">
            saves <Var>{total}</Var> vs price break
          </T>
        )}
      </span>
    );
  }

  const extra = formatCurrency(-saving.totalSaving);
  return (
    <span className={`${NOTE_CLASS} text-amber-700`}>
      {onList ? (
        <T context="replenishment board warning under a contract badge: the contract makes this line cost more than the supplier's list cost, by this amount">
          <Var>{extra}</Var> over list
        </T>
      ) : (
        <T context="replenishment board warning under a contract badge: the contract makes this line cost more than the supplier's price-break cost, by this amount">
          <Var>{extra}</Var> over price break
        </T>
      )}
    </span>
  );
}
